(() => {
  const CONTROLLER_KEY = '__freeCapCampaignController';
  const BOOTSTRAP_KEY = '__freeCapCampaignBootstrap';
  const GIFT_PROPERTY = '_free_cap_gift';

  if (window[BOOTSTRAP_KEY]) return;
  window[BOOTSTRAP_KEY] = true;

  class FreeCapCampaignController {
    constructor() {
      this.queue = Promise.resolve();
      this.lastCart = null;
      this.suppressNextCartChange = false;
      this.giftCodeWasApplied = false;
      this.pendingTasks = 0;
      this.campaignError = '';

      this.handleCartChange = this.handleCartChange.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleAfterMerge = this.handleAfterMerge.bind(this);

      document.addEventListener('on:cart:change', this.handleCartChange);
      document.addEventListener('on:cart:add', this.handleCartChange);
      document.addEventListener('on:cart:after-merge', this.handleAfterMerge);
      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('click', this.handleDocumentClick);

      this.enqueue(() => this.reconcile({ detectRemoval: false }));
    }

    get campaignRoots() {
      return [...document.querySelectorAll('.free-cap-campaign[data-free-cap-campaign]')];
    }

    getConfig() {
      const roots = this.campaignRoots;
      if (!roots.length) return null;

      let variantPayload = null;
      let variantRoot = null;

      for (const root of roots) {
        const script = root.querySelector('script[data-free-cap-variants]');
        if (!script) continue;

        try {
          const parsed = JSON.parse(script.textContent);
          const variants = Array.isArray(parsed) ? parsed : parsed.variants;
          if (Array.isArray(variants) && variants.length) {
            variantPayload = parsed;
            variantRoot = root;
            break;
          }
        } catch (error) {
          // Keep looking: the cart drawer and cart page can both contain campaign data.
        }
      }

      const root = variantRoot || roots[0];
      const rawVariants = Array.isArray(variantPayload) ? variantPayload : (variantPayload?.variants || []);
      const optionNames = Array.isArray(variantPayload?.optionNames) ? variantPayload.optionNames : [];
      const variants = rawVariants.map((variant) => {
        let options = Array.isArray(variant.options) ? variant.options : [];
        if (!options.length) {
          options = [variant.option1, variant.option2, variant.option3].filter((option) => option != null);
        }

        return {
          id: String(variant.id),
          available: variant.available !== false,
          options,
          featuredImage: variant.featuredImage || variant.image || ''
        };
      }).filter((variant) => variant.id);

      return {
        roots,
        enabled: roots.some((campaignRoot) => campaignRoot.dataset.enabled === 'true'),
        productId: String(variantPayload?.productId || root.dataset.productId || ''),
        productHandle: variantPayload?.productHandle || root.dataset.productHandle || '',
        optionNames,
        variants,
        code: root.dataset.discountCode || 'BUY3CAP',
        threshold: Math.max(1, parseInt(root.dataset.threshold, 10) || 3)
      };
    }

    enqueue(task) {
      const run = async () => {
        this.pendingTasks += 1;
        this.setCheckoutBusy(true);
        try {
          return await task();
        } finally {
          this.pendingTasks -= 1;
          if (this.pendingTasks === 0) this.setCheckoutBusy(false);
        }
      };
      this.queue = this.queue.then(run, run).catch((error) => {
        this.showError(error.message || 'The free cap could not be updated. Please try again.');
      });
      return this.queue;
    }

    handleCartChange() {
      if (this.suppressNextCartChange) {
        this.suppressNextCartChange = false;
        return;
      }

      this.enqueue(() => this.reconcile({ detectRemoval: true }));
    }

    handleAfterMerge() {
      const config = this.getConfig();
      if (!config || !this.lastCart) return;
      this.syncAddBackControls(this.lastCart, config);
      if (this.campaignError) this.renderCampaignError(this.campaignError);
    }

    handleDocumentChange(event) {
      const select = event.target.closest('[data-free-cap-option-index]');
      if (!select) return;

      const selector = select.closest('[data-free-cap-variant-selector]');
      if (!selector) return;

      this.enqueue(() => this.swapGiftVariant(selector));
    }

    handleDocumentClick(event) {
      if (this.pendingTasks > 0 && event.target.closest('[name="checkout"], .additional-checkout-buttons')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const addBack = event.target.closest('[data-free-cap-add-back]');
      if (!addBack) return;

      event.preventDefault();
      this.enqueue(async () => {
        const config = this.getConfig();
        if (!config) return;
        const cart = await this.fetchCart();
        this.setOptedOut(cart, config, false);
        await this.reconcile({ detectRemoval: false });
      });
    }

    async reconcile({ detectRemoval }) {
      const config = this.getConfig();
      if (!config) return;

      this.clearErrors();
      let cart = await this.fetchCart();
      let changed = false;
      let giftLines = this.getGiftLines(cart, config);
      let qualifyingQuantity = this.getQualifyingQuantity(cart, config);

      if (this.hasGiftDiscount(cart, config.code)) this.giftCodeWasApplied = true;

      if (!config.enabled) {
        if (giftLines.length) {
          cart = await this.removeGiftAndPreservePaidUnits(cart, config);
          changed = true;
        }
        const discountResult = await this.setGiftDiscount(cart, config, false);
        cart = discountResult.cart;
        changed = changed || discountResult.changed;
        this.lastCart = cart;
        if (changed) this.dispatchCartChange();
        return;
      }

      if (!config.variants.length) {
        this.showError('The free cap product is not configured.');
        return;
      }

      if (
        detectRemoval
        && this.lastCart
        && this.getGiftLines(this.lastCart, config).length
        && !giftLines.length
        && qualifyingQuantity >= config.threshold
      ) {
        this.setOptedOut(cart, config, true);
      }

      // More than one distinct tagged line can be created by racing cart requests.
      // Keep the first line and remove only the accidental duplicates.
      if (new Set(giftLines.map((line) => line.key)).size > 1) {
        cart = await this.removeDuplicateGiftLines(cart, config);
        changed = true;
        giftLines = this.getGiftLines(cart, config);
        qualifyingQuantity = this.getQualifyingQuantity(cart, config);
      }

      if (qualifyingQuantity < config.threshold) {
        this.setOptedOut(cart, config, false);
        if (giftLines.length) {
          cart = await this.removeGiftAndPreservePaidUnits(cart, config);
          changed = true;
        }

        const discountResult = await this.setGiftDiscount(cart, config, false);
        cart = discountResult.cart;
        changed = changed || discountResult.changed;
      } else {
        const optedOut = this.isOptedOut(cart, config);
        if (!giftLines.length && !optedOut) {
          cart = await this.addDefaultGift(cart, config);
          changed = true;
          giftLines = this.getGiftLines(cart, config);
        }

        if (giftLines.length) {
          const discountResult = await this.setGiftDiscount(cart, config, true);
          cart = discountResult.cart;
          changed = changed || discountResult.changed;

          cart = await this.fetchCart();
          if (!this.isGiftFree(cart, config)) {
            cart = await this.removeGiftAndPreservePaidUnits(cart, config);
            const removeDiscountResult = await this.setGiftDiscount(cart, config, false);
            cart = removeDiscountResult.cart;
            this.setOptedOut(cart, config, true);
            this.showError('The free cap could not be applied with the current cart discounts. Remove the other discount and try again.');
            changed = true;
          }
        } else {
          const discountResult = await this.setGiftDiscount(cart, config, false);
          cart = discountResult.cart;
          changed = changed || discountResult.changed;
          if (optedOut) this.showError('The free cap is not in your cart. Use “Add free cap” to restore it.');
        }
      }

      this.lastCart = cart;
      this.syncAddBackControls(cart, config);

      if (changed) this.dispatchCartChange();
    }

    getGiftLines(cart, config) {
      return (cart.items || []).filter((item) => (
        String(item.properties?.[GIFT_PROPERTY] || '') === config.code
        && (!config.productId || String(item.product_id) === config.productId)
      ));
    }

    getQualifyingQuantity(cart, config) {
      const itemQuantity = (cart.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const giftQuantity = this.getGiftLines(cart, config)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      return Math.max(0, itemQuantity - (giftQuantity > 0 ? 1 : 0));
    }

    async removeDuplicateGiftLines(cart, config) {
      const giftLines = this.getGiftLines(cart, config);
      const seenKeys = new Set();
      const uniqueLines = giftLines.filter((line) => {
        if (seenKeys.has(line.key)) return false;
        seenKeys.add(line.key);
        return true;
      });

      if (uniqueLines.length < 2) return cart;

      const updates = {};
      uniqueLines.slice(1).forEach((line) => {
        updates[line.key] = 0;
      });
      return this.request('cart/update.js', { updates });
    }

    async addDefaultGift(cart, config) {
      const candidates = config.variants.filter((variant) => variant.available);
      let lastError = null;

      for (const variant of candidates) {
        try {
          await this.request('cart/add.js', {
            items: [{
              id: Number(variant.id),
              quantity: 1,
              properties: { [GIFT_PROPERTY]: config.code }
            }]
          });
          return this.fetchCart();
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('The free cap is currently unavailable. Please try another option later.');
    }

    async removeGiftAndPreservePaidUnits(cart, config) {
      const giftLines = this.uniqueGiftLines(cart, config);
      const totalGiftQuantity = giftLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);

      if (totalGiftQuantity <= 1) {
        return this.removeCurrentGiftLines(cart, config);
      }

      // Exactly one unit is promotional. Additional quantities in the tagged line
      // are paid merchandise and must survive when eligibility is lost.
      const keeper = giftLines.find((line) => this.hasDiscountAllocation(line, config.code)) || giftLines[0];
      const paidItems = giftLines.map((line) => ({
        id: Number(line.variant_id || line.id),
        quantity: Math.max(0, Number(line.quantity || 0) - (line.key === keeper.key ? 1 : 0)),
        properties: this.withoutGiftProperty(line.properties)
      })).filter((item) => item.quantity > 0);

      const originalItems = giftLines.map((line) => ({
        id: Number(line.variant_id || line.id),
        quantity: Number(line.quantity || 0),
        properties: { ...(line.properties || {}) }
      }));

      const updates = {};
      giftLines.forEach((line) => {
        updates[line.key] = line.key === keeper.key ? 1 : 0;
      });

      let reducedCart = await this.request('cart/update.js', { updates });

      try {
        if (paidItems.length) await this.request('cart/add.js', { items: paidItems });
      } catch (error) {
        // Best-effort rollback protects paid quantities if Shopify rejects the move.
        try {
          const currentCart = await this.fetchCart();
          await this.removeCurrentGiftLines(currentCart, config);
          await this.request('cart/add.js', { items: originalItems });
        } catch (rollbackError) {
          // The original API error is more useful to the customer.
        }
        throw error;
      }

      reducedCart = await this.fetchCart();
      return this.removeCurrentGiftLines(reducedCart, config);
    }

    uniqueGiftLines(cart, config) {
      const byKey = new Map();
      this.getGiftLines(cart, config).forEach((line) => {
        const existing = byKey.get(line.key);
        if (existing) {
          existing.quantity += Number(line.quantity || 0);
        } else {
          byKey.set(line.key, { ...line, quantity: Number(line.quantity || 0) });
        }
      });
      return [...byKey.values()];
    }

    async removeCurrentGiftLines(cart, config) {
      const updates = {};
      this.uniqueGiftLines(cart, config).forEach((line) => {
        updates[line.key] = 0;
      });
      if (!Object.keys(updates).length) return cart;
      return this.request('cart/update.js', { updates });
    }

    async setGiftDiscount(cart, config, shouldApply) {
      const exposedCodes = this.getExposedDiscountCodes(cart);
      const hasGiftCode = this.hasGiftDiscount(cart, config.code);
      const hasGiftCodeEntry = exposedCodes.some((code) => code.toLowerCase() === config.code.toLowerCase());

      if (hasGiftCode) this.giftCodeWasApplied = true;
      if (shouldApply && hasGiftCode) return { cart, changed: false };
      if (!shouldApply && !hasGiftCodeEntry && !this.giftCodeWasApplied) return { cart, changed: false };

      const preservedCodes = exposedCodes.filter((code) => code.toLowerCase() !== config.code.toLowerCase());
      if (shouldApply) preservedCodes.push(config.code);

      const updatedCart = await this.request('cart/update.js', {
        discount: [...new Set(preservedCodes)].join(',')
      });
      this.giftCodeWasApplied = shouldApply;
      return { cart: updatedCart, changed: true };
    }

    getExposedDiscountCodes(cart) {
      const codes = [];
      const addCode = (value) => {
        const code = typeof value === 'string' ? value : value?.code;
        if (code && !codes.some((item) => item.toLowerCase() === String(code).toLowerCase())) {
          codes.push(String(code));
        }
      };

      (cart.discount_codes || []).forEach(addCode);
      [...(cart.discount_applications || []), ...(cart.cart_level_discount_applications || [])]
        .filter((application) => application?.type === 'discount_code')
        .forEach((application) => addCode(application.code || application.title));
      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          const application = allocation.discount_application;
          if (application?.type === 'discount_code') addCode(application.code || application.title);
        });
      });
      return codes;
    }

    hasGiftDiscount(cart, code) {
      const submittedCodeIsApplicable = (cart.discount_codes || []).some((discount) => {
        const submittedCode = typeof discount === 'string' ? discount : discount?.code;
        return String(submittedCode || '').toLowerCase() === code.toLowerCase()
          && discount?.applicable !== false;
      });
      if (submittedCodeIsApplicable) return true;

      const applications = [
        ...(cart.discount_applications || []),
        ...(cart.cart_level_discount_applications || [])
      ];
      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          if (allocation.discount_application) applications.push(allocation.discount_application);
        });
      });
      return applications.some((application) => (
        String(application?.code || application?.title || '').toLowerCase() === code.toLowerCase()
      ));
    }

    hasDiscountAllocation(line, code) {
      return (line.line_level_discount_allocations || []).some((allocation) => (
        String(allocation.discount_application?.code || allocation.discount_application?.title || '').toLowerCase()
          === code.toLowerCase()
      ));
    }

    isGiftFree(cart, config) {
      return this.getGiftLines(cart, config).some((line) => {
        if (Number(line.final_price) === 0) return true;
        return (line.line_level_discount_allocations || []).some((allocation) => {
          const allocationCode = String(
            allocation.discount_application?.code || allocation.discount_application?.title || ''
          ).toLowerCase();
          return allocationCode === config.code.toLowerCase()
            && Number(allocation.amount || 0) >= Number(line.original_price || line.price || 0);
        });
      });
    }

    async swapGiftVariant(selector) {
      const config = this.getConfig();
      if (!config) return;

      const currentVariantId = String(selector.dataset.currentVariantId || '');
      const currentVariant = config.variants.find((variant) => variant.id === currentVariantId);
      const desiredOptions = currentVariant ? [...currentVariant.options] : [];

      selector.querySelectorAll('[data-free-cap-option-index]').forEach((select) => {
        desiredOptions[Number(select.dataset.freeCapOptionIndex)] = select.value;
      });

      const targetVariant = config.variants.find((variant) => (
        variant.available
        && variant.options.length === desiredOptions.length
        && variant.options.every((option, index) => option === desiredOptions[index])
      ));

      if (!targetVariant) {
        this.restoreSelector(selector, currentVariant);
        this.showError('This color and size combination is unavailable.', selector);
        return;
      }
      if (targetVariant.id === currentVariantId) return;

      this.setSelectorBusy(selector, true);
      this.clearErrors(selector);

      let targetAdded = false;
      let oldLineRemoved = false;

      try {
        let cart = await this.fetchCart();
        const lineKey = selector.dataset.lineKey;
        const oldLine = this.getGiftLines(cart, config).find((line) => line.key === lineKey)
          || this.getGiftLines(cart, config).find((line) => String(line.variant_id || line.id) === currentVariantId)
          || this.getGiftLines(cart, config)[0];

        if (!oldLine) throw new Error('The free cap is no longer in the cart. Refresh the cart and try again.');

        await this.request('cart/add.js', {
          items: [{
            id: Number(targetVariant.id),
            quantity: Number(oldLine.quantity || 1),
            properties: { ...(oldLine.properties || {}), [GIFT_PROPERTY]: config.code }
          }]
        });
        targetAdded = true;

        cart = await this.fetchCart();
        const latestOldLine = this.getGiftLines(cart, config).find((line) => (
          String(line.variant_id || line.id) === String(oldLine.variant_id || oldLine.id)
        ));

        if (latestOldLine) {
          cart = await this.request('cart/change.js', { id: latestOldLine.key, quantity: 0 });
        }
        oldLineRemoved = true;

        const discountResult = await this.setGiftDiscount(cart, config, true);
        cart = discountResult.cart;
        this.lastCart = cart;
        this.syncAddBackControls(cart, config);
        this.dispatchCartChange();
      } catch (error) {
        if (targetAdded && !oldLineRemoved) {
          try {
            let rollbackCart = await this.fetchCart();
            const addedLine = this.getGiftLines(rollbackCart, config).find((line) => (
              String(line.variant_id || line.id) === targetVariant.id
            ));
            if (addedLine) {
              rollbackCart = await this.request('cart/change.js', { id: addedLine.key, quantity: 0 });
            }
            this.lastCart = rollbackCart;
          } catch (rollbackError) {
            // Reconciliation on the next cart event will remove a duplicate line.
          }
          this.restoreSelector(selector, currentVariant);
        } else if (oldLineRemoved) {
          try {
            this.lastCart = await this.fetchCart();
            this.dispatchCartChange();
          } catch (refreshError) {
            // The cart mutation succeeded; the next theme refresh will render it.
          }
        } else {
          this.restoreSelector(selector, currentVariant);
        }
        this.showError(error.message || 'The free cap could not be updated.', selector);
      } finally {
        this.setSelectorBusy(selector, false);
      }
    }

    restoreSelector(selector, variant) {
      if (!variant) return;
      selector.querySelectorAll('[data-free-cap-option-index]').forEach((select) => {
        select.value = variant.options[Number(select.dataset.freeCapOptionIndex)] || '';
      });
    }

    setSelectorBusy(selector, busy) {
      selector.setAttribute('aria-busy', String(busy));
      selector.querySelectorAll('select').forEach((select) => {
        select.disabled = busy;
      });
    }

    withoutGiftProperty(properties) {
      const result = { ...(properties || {}) };
      delete result[GIFT_PROPERTY];
      return result;
    }

    storageKey(cart, config) {
      return `free-cap-opt-out:${cart.token || 'cart'}:${config.code}`;
    }

    isOptedOut(cart, config) {
      try {
        return window.sessionStorage.getItem(this.storageKey(cart, config)) === '1';
      } catch (error) {
        return false;
      }
    }

    setOptedOut(cart, config, optedOut) {
      try {
        if (optedOut) {
          window.sessionStorage.setItem(this.storageKey(cart, config), '1');
        } else {
          window.sessionStorage.removeItem(this.storageKey(cart, config));
        }
      } catch (error) {
        // A disabled sessionStorage should not block normal cart behavior.
      }
      this.syncAddBackControls(cart, config);
    }

    syncAddBackControls(cart, config) {
      const eligible = this.getQualifyingQuantity(cart, config) >= config.threshold;
      const show = eligible && this.isOptedOut(cart, config) && !this.getGiftLines(cart, config).length;
      document.querySelectorAll('[data-free-cap-add-back]').forEach((control) => {
        control.hidden = !show;
        control.setAttribute('aria-hidden', String(!show));
      });
    }

    showError(message, scope = document) {
      if (scope === document) this.campaignError = message;
      const nodes = scope.matches?.('[data-free-cap-error]')
        ? [scope]
        : [...scope.querySelectorAll('[data-free-cap-error]')];
      nodes.forEach((node) => {
        node.textContent = message;
        node.hidden = false;
      });
      if (!nodes.length) console.warn(`[Free cap] ${message}`); // eslint-disable-line no-console
    }

    renderCampaignError(message) {
      document.querySelectorAll('.free-cap-campaign--cart [data-free-cap-error]').forEach((node) => {
        node.textContent = message;
        node.hidden = false;
      });
      document.querySelectorAll('.free-cap-campaign--cart [data-free-cap-progress-text]').forEach((node) => {
        node.textContent = 'The free cap is not applied yet.';
      });
    }

    clearErrors(scope = document) {
      if (scope === document) this.campaignError = '';
      const nodes = scope.matches?.('[data-free-cap-error]')
        ? [scope]
        : [...scope.querySelectorAll('[data-free-cap-error]')];
      nodes.forEach((node) => {
        node.textContent = '';
        node.hidden = true;
      });
    }

    setCheckoutBusy(busy) {
      document.documentElement.classList.toggle('free-cap-reconciling', busy);
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (busy) {
          button.dataset.freeCapWasDisabled = String(button.disabled);
          button.disabled = true;
        } else if (button.dataset.freeCapWasDisabled === 'false') {
          button.disabled = false;
          delete button.dataset.freeCapWasDisabled;
        }
      });
    }

    dispatchCartChange() {
      this.suppressNextCartChange = true;
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    }

    async fetchCart() {
      return this.request('cart.js', null, 'GET');
    }

    async request(path, body, method = 'POST') {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root.endsWith('/') ? root : `${root}/`}${path}`;
      const options = {
        method,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      };
      if (body !== null) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }

      const response = await fetch(url, options);
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        // The status-based fallback below is sufficient for a non-JSON error.
      }
      if (!response.ok) {
        const message = data?.description || data?.message || `Cart request failed (${response.status}).`;
        throw new Error(message);
      }
      return data;
    }
  }

  const bootstrap = () => {
    const campaignRoot = document.querySelector('.free-cap-campaign[data-free-cap-campaign]');
    if (campaignRoot && !window[CONTROLLER_KEY]) {
      window[CONTROLLER_KEY] = new FreeCapCampaignController();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
  document.addEventListener('shopify:section:load', bootstrap);
  document.addEventListener('on:cart:after-merge', bootstrap);
})();
